import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  Building,
  Check,
  Cloud,
  Cpu,
  Database,
  Factory,
  Loader2,
  Radio,
  Webhook,
  X,
} from "lucide-react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import {
  type Device,
  type SourceCatalogEntry,
  type SourceField,
  createDevice,
  listSourceTypes,
} from "../../lib/instrumentationApi";
import { SOURCE_LOGOS } from "./sourceLogos";

/** Lucide icon lookup keyed by the catalog entry's `icon` field. Used as a
 *  fallback when there's no branded logo in SOURCE_LOGOS for the source. */
const ICON_BY_KEY: Record<string, typeof Activity> = {
  Activity,
  Radio,
  Cloud,
  Factory,
  Cpu,
  Building,
  Webhook,
  Database,
};

import { SearchableAssetPicker, type AssetOption } from "./SearchableAssetPicker";

interface CreateDeviceWizardProps {
  isOpen: boolean;
  onClose: () => void;
  virtualSiteId: string;
  /** Same-site asset options to surface in the optional linkage picker.
   *  Passed in from the page so we don't refetch — the page already loads
   *  them for the tree's asset pills. */
  assets?: AssetOption[];
  onCreated: (device: Device) => void;
}

/** Two-step wizard: pick a source, then fill in the device + connection
 *  details. Replaces the previous one-screen create-device modal so the
 *  user is forced to think about where the data is going to come from. */
export function CreateDeviceWizard({
  isOpen,
  onClose,
  virtualSiteId,
  assets = [],
  onCreated,
}: CreateDeviceWizardProps) {
  const [catalog, setCatalog] = useState<SourceCatalogEntry[]>([]);
  const [isLoadingCatalog, setIsLoadingCatalog] = useState(false);
  const [selectedSource, setSelectedSource] = useState<SourceCatalogEntry | null>(null);
  const [step, setStep] = useState<"source" | "details">("source");

  // Form state — device metadata + dynamic source-config fields. The
  // catalog declares each source's required fields so we render them
  // generically rather than building a separate form per integration.
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [deviceType, setDeviceType] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [model, setModel] = useState("");
  const [serial, setSerial] = useState("");
  const [assetId, setAssetId] = useState("");
  const [sourceFieldValues, setSourceFieldValues] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setIsLoadingCatalog(true);
    listSourceTypes()
      .then((res) => {
        if (cancelled) return;
        setCatalog(res);
      })
      .catch(() => {
        if (cancelled) return;
        setCatalog([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingCatalog(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  function reset() {
    setSelectedSource(null);
    setStep("source");
    setName("");
    setDescription("");
    setDeviceType("");
    setManufacturer("");
    setModel("");
    setSerial("");
    setAssetId("");
    setSourceFieldValues({});
    setError(null);
  }

  function handleClose() {
    if (isSaving) return;
    reset();
    onClose();
  }

  function selectSource(src: SourceCatalogEntry) {
    setSelectedSource(src);
    setSourceFieldValues({});
    setStep("details");
  }

  const groupedCatalog = useMemo(() => {
    const groups = new Map<string, SourceCatalogEntry[]>();
    for (const e of catalog) {
      if (!groups.has(e.category)) groups.set(e.category, []);
      groups.get(e.category)!.push(e);
    }
    return Array.from(groups.entries());
  }, [catalog]);

  async function handleCreate() {
    if (!selectedSource) return;
    if (!name.trim()) {
      setError("Device name is required.");
      return;
    }
    // Enforce required source-config fields client-side so the user gets
    // immediate feedback rather than waiting for a backend 422 round trip.
    for (const f of selectedSource.fields) {
      if (f.required && !sourceFieldValues[f.key]?.trim()) {
        setError(`"${f.label}" is required.`);
        return;
      }
    }
    setIsSaving(true);
    setError(null);
    try {
      const payload = {
        virtual_site_id: virtualSiteId,
        name: name.trim(),
        description: description.trim() || null,
        device_type: deviceType.trim() || null,
        manufacturer: manufacturer.trim() || null,
        model: model.trim() || null,
        serial_number: serial.trim() || null,
        asset_id: assetId || null,
        source_type: selectedSource.key,
        source_config: Object.fromEntries(
          Object.entries(sourceFieldValues).filter(([, v]) => v.trim() !== ""),
        ),
      };
      const device = await createDevice(payload);
      onCreated(device);
      reset();
      onClose();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e.response?.data?.detail || "Failed to create device.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        step === "source"
          ? "Connect a data source"
          : `New device · ${selectedSource?.label ?? "Source"}`
      }
      size="lg"
    >
      {step === "source" ? (
        <div className="space-y-3">
          <p className="text-xs text-surface-400">
            Pick which IoT platform will feed this device's sensors. You can change
            this later from the device settings.
          </p>
          {isLoadingCatalog ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-surface-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading source catalog…
            </div>
          ) : (
            <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
              {groupedCatalog.map(([category, entries]) => (
                <div key={category}>
                  <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-surface-500">
                    {category}
                  </div>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    {entries.map((src) => {
                      // Branded logo first; Lucide icon as a generic fallback.
                      const Logo = SOURCE_LOGOS[src.key];
                      const FallbackIcon = ICON_BY_KEY[src.icon] || Cpu;
                      const isSelected = selectedSource?.key === src.key;
                      return (
                        <button
                          key={src.key}
                          onClick={() => selectSource(src)}
                          className={
                            "group flex items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-all " +
                            (isSelected
                              ? "border-brand-500/60 bg-brand-500/10 ring-1 ring-brand-500/40"
                              : "border-surface-700/60 bg-surface-900/40 hover:border-brand-500/50 hover:bg-brand-500/5")
                          }
                        >
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center">
                            {Logo ? (
                              <Logo size={32} className="rounded-md ring-1 ring-white/10" />
                            ) : (
                              <div
                                className={
                                  "flex h-8 w-8 items-center justify-center rounded-md ring-1 ring-white/10 " +
                                  (isSelected
                                    ? "bg-brand-500/30 text-brand-200"
                                    : "bg-surface-800 text-surface-300 group-hover:bg-brand-500/20 group-hover:text-brand-200")
                                }
                              >
                                <FallbackIcon className="h-4 w-4" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 text-sm font-medium text-surface-100">
                              {src.label}
                              {isSelected && <Check className="h-3.5 w-3.5 text-brand-300" />}
                            </div>
                            <p className="mt-0.5 text-[11px] leading-snug text-surface-400">
                              {src.description}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {error && (
            <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
              {error}
            </div>
          )}
          {selectedSource && (
            <div className="flex items-start gap-3 rounded-lg border border-surface-700/60 bg-surface-900/40 px-3 py-2.5 text-xs">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center">
                {(() => {
                  const Logo = SOURCE_LOGOS[selectedSource.key];
                  if (Logo) {
                    return <Logo size={28} className="rounded-md ring-1 ring-white/10" />;
                  }
                  const Icon = ICON_BY_KEY[selectedSource.icon] || Cpu;
                  return (
                    <div className="flex h-7 w-7 items-center justify-center rounded-md bg-brand-500/20 text-brand-200">
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                  );
                })()}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 text-surface-100">
                  <span className="font-medium">{selectedSource.label}</span>
                  <span className="rounded bg-surface-800 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-surface-400">
                    {selectedSource.category}
                  </span>
                </div>
                <p className="mt-0.5 text-surface-400">{selectedSource.description}</p>
              </div>
              <button
                onClick={() => setStep("source")}
                className="ml-2 rounded p-1 text-surface-400 hover:bg-surface-800 hover:text-surface-200"
                title="Pick a different source"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          <div className="grid max-h-[55vh] grid-cols-1 gap-3 overflow-y-auto pr-1 md:grid-cols-2">
            <Input
              label="Name *"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Pump-A vibration monitor"
              disabled={isSaving}
            />
            <Input
              label="Type"
              value={deviceType}
              onChange={(e) => setDeviceType(e.target.value)}
              placeholder="e.g. vibration_logger"
              disabled={isSaving}
            />
            <Input
              label="Manufacturer"
              value={manufacturer}
              onChange={(e) => setManufacturer(e.target.value)}
              disabled={isSaving}
            />
            <Input
              label="Model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              disabled={isSaving}
            />
            <Input
              label="Serial number"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              placeholder="Unique within this site"
              disabled={isSaving}
            />
            <SearchableAssetPicker
              label="Linked asset"
              assets={assets}
              value={assetId}
              onChange={(id) => setAssetId(id ?? "")}
              disabled={isSaving}
              help="New child sensors inherit this asset by default."
            />
            <div className="md:col-span-2">
              <Input
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isSaving}
              />
            </div>

            {selectedSource && selectedSource.fields.length > 0 && (
              <div className="md:col-span-2">
                <div className="mb-1.5 mt-2 text-[10px] font-semibold uppercase tracking-wider text-surface-500">
                  Connection settings
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {selectedSource.fields.map((field) => (
                    <SourceFieldInput
                      key={field.key}
                      field={field}
                      value={sourceFieldValues[field.key] ?? ""}
                      onChange={(v) =>
                        setSourceFieldValues((prev) => ({ ...prev, [field.key]: v }))
                      }
                      disabled={isSaving}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setStep("source")}
              disabled={isSaving}
              className="text-xs text-surface-400 hover:text-surface-200"
            >
              ← Change source
            </button>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
                Cancel
              </Button>
              <Button onClick={handleCreate} isLoading={isSaving} disabled={!name.trim()}>
                Create device
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

function SourceFieldInput({
  field,
  value,
  onChange,
  disabled,
}: {
  field: SourceField;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  if (field.type === "select" && field.options) {
    return (
      <div>
        <label className="mb-1.5 block text-sm font-medium text-surface-200">
          {field.label}
          {field.required && <span className="ml-1 text-red-400">*</span>}
        </label>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="w-full rounded-lg border border-surface-700 bg-surface-900 px-3 py-2 text-sm text-surface-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="">Select…</option>
          {field.options.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        {field.help && <p className="mt-1 text-[10px] text-surface-500">{field.help}</p>}
      </div>
    );
  }
  const inputType =
    field.type === "password"
      ? "password"
      : field.type === "number"
        ? "number"
        : field.type === "url"
          ? "url"
          : "text";
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-surface-200">
        {field.label}
        {field.required && <span className="ml-1 text-red-400">*</span>}
      </label>
      <input
        type={inputType}
        value={value}
        placeholder={field.placeholder}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full rounded-lg border border-surface-700 bg-surface-900 px-3 py-2 text-sm text-surface-100 placeholder:text-surface-600 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      />
      {field.help && <p className="mt-1 text-[10px] text-surface-500">{field.help}</p>}
    </div>
  );
}
